import type { CharacterMotor } from '../physics/CharacterMotor';
import type { InputMap } from '../input/InputMap';

/** Horizontal swim gaits (m/s). Sprint swim drains stamina. */
export const SWIM_SPEED = 1.4;
export const SWIM_SPRINT = 1.9;
export const SWIM_UP = 1.15;
export const SWIM_DIVE = 0.85;
export const SWIM_DRIFT = 0.28;

/** Crash Coast ocean starts south of the wet-sand line. */
export const OCEAN_Z = 14;
export const OCEAN_WATER_Y = 1.15;

/** Glassvein sheet — Riverlands, dock excluded. */
export const RIVER_WATER_Y = 0.9;
export const RIVER_MIN_X = 58;
export const RIVER_MAX_X = 108;
export const RIVER_MIN_Z = -12;
export const RIVER_MAX_Z = -1;
export const RIVER_DOCK = { x: 80, z: -10 };
export const RIVER_DOCK_HX = 4;
export const RIVER_DOCK_HZ = 2.4;

/** Capsule center sits this far below the surface so the chest rides the plane. */
export const CHEST_OFFSET = 0.25;
export const SWIM_HEADROOM = 1.55;
export const SWIM_BED_Y = -0.2;
export const SWIM_LINE_SLACK = 0.15;

/** 100 stamina / 8 s sprint-swim. Regen after 2 s out of sprint-swim. */
export const SWIM_STAMINA_S = 8;
export const SWIM_STAMINA_PER_S = 100 / SWIM_STAMINA_S;
export const SWIM_REGEN_DELAY = 2;
export const SWIM_REGEN_PER_S = 100 / SWIM_STAMINA_S;
export const DROWN_DELAY_S = 3;
export const DROWN_HP_PER_S = 8;

export type WaterKind = 'ocean' | 'river';

export type WaterHit = {
  kind: WaterKind;
  surfaceY: number;
};

export type SwimStamina = {
  stamina: number;
  spendStamina(amount: number): boolean;
  addStamina?(amount: number): void;
};

export type SwimBody = {
  yaw: number;
  vx: number;
  vz: number;
  vy: number;
  grounded: boolean;
};

export type SwimInput = {
  moveX: number;
  moveZ: number;
  sprint: boolean;
  sneak: boolean;
  jumpDown: boolean;
};

export function inOcean(x: number, y: number, z: number): boolean {
  void x;
  return z > OCEAN_Z && y > SWIM_BED_Y && y < OCEAN_WATER_Y + SWIM_HEADROOM;
}

export function onRiverDock(x: number, z: number): boolean {
  return Math.abs(x - RIVER_DOCK.x) <= RIVER_DOCK_HX && Math.abs(z - RIVER_DOCK.z) <= RIVER_DOCK_HZ;
}

export function inRiverWater(x: number, y: number, z: number): boolean {
  if (y <= SWIM_BED_Y || y >= RIVER_WATER_Y + SWIM_HEADROOM) {
    return false;
  }
  if (x < RIVER_MIN_X || x > RIVER_MAX_X || z < RIVER_MIN_Z || z > RIVER_MAX_Z) {
    return false;
  }
  return !onRiverDock(x, z);
}

export function sampleWater(x: number, y: number, z: number): WaterHit | null {
  if (y < -8) {
    return null;
  }
  if (inOcean(x, y, z)) {
    return { kind: 'ocean', surfaceY: OCEAN_WATER_Y };
  }
  if (inRiverWater(x, y, z)) {
    return { kind: 'river', surfaceY: RIVER_WATER_Y };
  }
  return null;
}

export function swimLine(surfaceY: number): number {
  return surfaceY - CHEST_OFFSET;
}

export function swimGait(sprint: boolean, staminaOk: boolean): number {
  return sprint && staminaOk ? SWIM_SPRINT : SWIM_SPEED;
}

export function wishSwim(
  input: SwimInput,
  yaw: number,
  staminaOk: boolean,
): { vx: number; vz: number; vy: number; sprinting: boolean } {
  let ix = input.moveX;
  let iz = input.moveZ;
  const mag = Math.hypot(ix, iz);
  if (mag > 1) {
    ix /= mag;
    iz /= mag;
  }
  const sprinting = input.sprint && staminaOk && mag > 0;
  const gait = swimGait(sprinting, true);
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  const vx = mag === 0 ? 0 : (ix * cos + iz * -sin) * gait;
  const vz = mag === 0 ? 0 : (ix * -sin + iz * -cos) * gait;
  let vy = -SWIM_DRIFT;
  if (input.jumpDown) {
    vy = SWIM_UP;
  } else if (input.sneak) {
    vy = -SWIM_DIVE;
  }
  return { vx, vz, vy, sprinting };
}

/** Keep the chest on the water plane. Never sink through the swim line. */
export function holdChest(
  centerY: number,
  surfaceY: number,
  vy: number,
  dt: number,
): { y: number; vy: number } {
  const line = swimLine(surfaceY);
  let next = centerY + vy * dt;
  let nextVy = vy;
  if (next < line) {
    next = line;
    if (nextVy < 0) {
      nextVy = 0;
    }
  }
  const ceiling = surfaceY + SWIM_HEADROOM - 0.05;
  if (next > ceiling) {
    next = ceiling;
    if (nextVy > 0) {
      nextVy = 0;
    }
  }
  return { y: next, vy: nextVy };
}

export function onSwimLine(centerY: number, surfaceY: number, slack = SWIM_LINE_SLACK): boolean {
  return Math.abs(centerY - swimLine(surfaceY)) <= slack;
}

export class Swim {
  active = false;
  entered = false;
  kind: WaterKind | null = null;
  surfaceY = OCEAN_WATER_Y;
  emptyS = 0;
  private regenWait = 0;

  sample(pos: { x: number; y: number; z: number }): WaterHit | null {
    return sampleWater(pos.x, pos.y, pos.z);
  }

  /** Drain sprint-swim; regen after a short rest. Empty stamina while swimming counts toward drown. */
  tickStamina(dt: number, sprinting: boolean, stamina?: SwimStamina | null): boolean {
    if (!stamina) {
      return true;
    }
    if (sprinting) {
      this.regenWait = 0;
      stamina.spendStamina(SWIM_STAMINA_PER_S * dt);
    } else {
      this.regenWait += dt;
      if (this.regenWait >= SWIM_REGEN_DELAY) {
        stamina.addStamina?.(SWIM_REGEN_PER_S * dt);
      }
    }
    if (stamina.stamina <= 0) {
      this.emptyS += dt;
      return false;
    }
    this.emptyS = 0;
    return true;
  }

  drive(
    dt: number,
    input: InputMap,
    body: SwimBody,
    motor: CharacterMotor,
    water: WaterHit,
    stamina?: SwimStamina | null,
  ): void {
    this.active = true;
    this.entered = true;
    this.kind = water.kind;
    this.surfaceY = water.surfaceY;
    motor.setSneak(false);
    input.consumeJump();

    const staminaOk = (stamina?.stamina ?? 1) > 0;
    const wish = wishSwim(input, body.yaw, staminaOk);
    this.tickStamina(dt, wish.sprinting, stamina);
    const stillOk = (stamina?.stamina ?? 1) > 0;
    const used = wish.sprinting && stillOk ? wish : wishSwim(input, body.yaw, stillOk);

    body.vx = used.vx;
    body.vz = used.vz;
    const held = holdChest(motor.translation().y, water.surfaceY, used.vy, dt);
    body.vy = held.vy;
    motor.move(body.vx * dt, held.y - motor.translation().y, body.vz * dt, body.vy);

    const pos = motor.translation();
    const line = swimLine(water.surfaceY);
    if (pos.y < line - 0.001) {
      motor.setTranslation(pos.x, line, pos.z);
      body.vy = 0;
    }
    body.grounded = motor.grounded;
  }

  leave(): void {
    this.active = false;
    this.kind = null;
    this.emptyS = 0;
  }
}
